import { ConfigurationType, ExistingSystemType } from 'data/types';
import { produce } from 'immer';
import { getDuctedPairZone } from 'utils/ducted-design';
import { DuctedNewSystemSlotLink } from 'utils/ducted-new-systems-types';

export function linkedExistingSystemIds(
  configuration: Pick<ConfigurationType, 'condensers'>,
  excludedCondenserId?: string
): Set<string> {
  const linked = new Set<string>();
  configuration.condensers.forEach((condenser) => {
    if (condenser.uniqueId === excludedCondenserId) return;
    const existingSystemId = getDuctedPairZone(condenser)?.existingSystemId;
    if (existingSystemId) linked.add(existingSystemId);
  });
  return linked;
}

export function nextUnlinkedExistingSystemId(
  configuration: Pick<ConfigurationType, 'condensers'>,
  eligibleSystems: readonly ExistingSystemType[]
): string | undefined {
  const linked = linkedExistingSystemIds(configuration);
  return eligibleSystems.find((system) => !linked.has(system.uniqueId))
    ?.uniqueId;
}

export function withDuctedPairLinks(
  configurations: ConfigurationType[],
  configurationId: string,
  links: readonly DuctedNewSystemSlotLink[]
): ConfigurationType[] {
  return produce(configurations, (draft) => {
    const configuration = draft.find(
      (candidate) => candidate.uniqueId === configurationId
    );
    if (!configuration) return;
    links.forEach(({ condenserId, existingSystemId }) => {
      const condenser = configuration.condensers.find(
        (candidate) => candidate.uniqueId === condenserId
      );
      const zone = condenser ? getDuctedPairZone(condenser) : null;
      if (!zone || zone.existingSystemId === existingSystemId) return;
      zone.existingSystemId = existingSystemId;
      zone.modified = true;
      configuration.modified = true;
    });
  });
}

export function withoutOrphanedDuctedPairLinks(
  configurations: ConfigurationType[],
  existingSystemIds: ReadonlySet<string>
): ConfigurationType[] {
  return produce(configurations, (draft) => {
    for (const configuration of draft) {
      for (const condenser of configuration.condensers) {
        for (const zone of condenser.zones) {
          if (
            zone.existingSystemId &&
            !existingSystemIds.has(zone.existingSystemId)
          ) {
            delete zone.existingSystemId;
            zone.modified = true;
            configuration.modified = true;
          }
        }
      }
    }
  });
}
